import i18n from '@/plugins/i18n'

export const INSIGHTS_TABS = {
  CLAIMS: 'CLAIMS',
  VARIATIONS: 'VARIATIONS',
  OVERVIEW: 'OVERVIEW'
}

export const variationsStatusLabelsMap = {
  PEN: 'Pending',
  FIN: 'Finalized',
  ONG: 'Ongoing',
  DEC: 'Declined',
  CLO: 'Closed',
  DRA: 'DRA',
  C2V: 'Converted to VO',
  NPR: 'Not Progressed'
}

export const COLOR_PALETTE = ['#4e79a7', '#f28e2c', '#59a14f', '#e15759', '#76b7b2', '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab']

export const getDefaultChartTextStyle = (fullView = false) => ({
  fontSize: fullView ? 16 : 12,
  fontWeight: 'bold',
  overflow: 'break'
})

export const isDataEmpty = (resultSet) => {
  const pivot = resultSet?.chartPivot() || []
  if (!pivot.length) return true

  return pivot.every(obj =>
    Object.keys(obj).every(key =>
      (key === 'x' || key === 'xValues') ? true : obj[key] === 0
    )
  )
}

const createBaseNoDataOptions = (fullView) => ({
  graphic: [{
    type: 'text',
    left: 'center',
    top: 'middle',
    style: {
      text: i18n.t('insights.filters.noData'),
      ...getDefaultChartTextStyle(fullView),
      fill: '#666'
    }
  }],
  legend: { show: false }
})

const handleAxis = (axis) => {
  if (!axis) return null
  return Array.isArray(axis)
    ? axis.map(a => ({ ...a, show: false }))
    : { ...axis, show: false }
}

export const getPieNoDataOptions = (resultSet, fullView, option) => {
  if (!isDataEmpty(resultSet)) return null

  return {
    ...createBaseNoDataOptions(fullView),
    series: option.series.map(series => ({
      ...series,
      radius: 0,
      label: { ...series.label, show: false },
      emphasis: { ...series.emphasis, scale: false }
    }))
  }
}

export const getBarNoDataOptions = (resultSet, fullView, option) => {
  if (!isDataEmpty(resultSet)) return null

  return {
    ...createBaseNoDataOptions(fullView),
    xAxis: handleAxis(option.xAxis),
    yAxis: handleAxis(option.yAxis)
  }
}

export const CHART_NAMES = {
  CLAIMS_REPORTED_BY: 'CLAIMS_REPORTED_BY',
  CLAIMS_VALUE_CONTRACTOR: 'CLAIMS_VALUE_CONTRACTOR',
  CLAIMS_VALUE_EMPLOYER: 'CLAIMS_VALUE_EMPLOYER',
  CLAIMS_VALUE_AND_COUNT: 'CLAIMS_VALUE_AND_COUNT',
  CLAIMS_PER_STATUS: 'CLAIMS_PER_STATUS',
  EOT_PER_FINALIZED_CLAIMS: 'EOT_PER_FINALIZED_CLAIMS',
  CLAIMS_TREND: 'CLAIMS_TREND',
  VARIATIONS_PER_STATUS: 'VARIATIONS_PER_STATUS',
  VARIATIONS_PER_STAGE: 'VARIATIONS_PER_STAGE',
  VARIATIONS_PER_ROOT_CAUSES: 'VARIATIONS_PER_ROOT_CAUSES',
  VARIATIONS_TIME_IMPACT_PER_ROOT_CAUSES: 'VARIATIONS_TIME_IMPACT_PER_ROOT_CAUSES',
  VARIATIONS_FINANCIAL_IMPACT_PER_ROOT_CAUSES: 'VARIATIONS_FINANCIAL_IMPACT_PER_ROOT_CAUSES',
  VARIATIONS_VALUE_AND_COUNT: 'VARIATIONS_VALUE_AND_COUNT',
  VARIATIONS_ACTUAL_IMPACT_IN_DAYS: 'VARIATIONS_ACTUAL_IMPACT_IN_DAYS',
  CONTRACTS_PAID_AND_UNPAID: 'CONTRACTS_PAID_AND_UNPAID',
  CONTRACTS_ONGOING_AND_CLOSED: 'CONTRACTS_ONGOING_AND_CLOSED',
  CONTRACTS_SIGNED_AND_ADJUSTED_PRICES: 'CONTRACTS_SIGNED_AND_ADJUSTED_PRICES',
  LDS_VALUE_AND_COUNT_CONTRACTOR: 'LDS_VALUE_AND_COUNT_CONTRACTOR'
}

export const CHART_COMPONENTS = {
  [CHART_NAMES.CLAIMS_REPORTED_BY]: 'ClaimsReportedByChart',
  [CHART_NAMES.CLAIMS_VALUE_CONTRACTOR]: 'ClaimsValueContractorChart',
  [CHART_NAMES.CLAIMS_VALUE_EMPLOYER]: 'ClaimsValueEmployerChart',
  [CHART_NAMES.CLAIMS_VALUE_AND_COUNT]: 'ClaimsValueAndCountChart',
  [CHART_NAMES.CLAIMS_PER_STATUS]: 'ClaimsPerStatusChart',
  [CHART_NAMES.EOT_PER_FINALIZED_CLAIMS]: 'EotPerFinalizedClaimsChart',
  [CHART_NAMES.CLAIMS_TREND]: 'ClaimsTrendChart',
  [CHART_NAMES.VARIATIONS_PER_STATUS]: 'VariationsPerStatusChart',
  [CHART_NAMES.VARIATIONS_PER_STAGE]: 'VariationsPerStageChart',
  [CHART_NAMES.VARIATIONS_PER_ROOT_CAUSES]: 'VariationsPerRootCausesChart',
  [CHART_NAMES.VARIATIONS_TIME_IMPACT_PER_ROOT_CAUSES]: 'VariationsTimeImpactPerRootCausesChart',
  [CHART_NAMES.VARIATIONS_FINANCIAL_IMPACT_PER_ROOT_CAUSES]: 'VariationsFinancialImpactPerRootCausesChart',
  [CHART_NAMES.VARIATIONS_VALUE_AND_COUNT]: 'VariationsValueAndCountChart',
  [CHART_NAMES.VARIATIONS_ACTUAL_IMPACT_IN_DAYS]: 'VariationsActualImpactInDaysChart',
  [CHART_NAMES.CONTRACTS_PAID_AND_UNPAID]: 'ContractsPaidUnpaidChart',
  [CHART_NAMES.CONTRACTS_ONGOING_AND_CLOSED]: 'ContractsOngoingClosedChart',
  [CHART_NAMES.CONTRACTS_SIGNED_AND_ADJUSTED_PRICES]: 'ContractsSignedAdjustedPricesChart',
  [CHART_NAMES.LDS_VALUE_AND_COUNT_CONTRACTOR]: 'LDsValueAndCountContractorChart'
}

export const INSIGHTS_CHARTS = {
  [CHART_NAMES.CLAIMS_REPORTED_BY]: {
    component: CHART_COMPONENTS.CLAIMS_REPORTED_BY, // This is the chart component name that will be rendered.
    chartWidth: '240px', // This is where you can set a fixed width for the chart. if this property is missing, the width will be default.
    defaultTimeDimension: 'variations.receiveddate', // This is the default time dimension for the chart, applies for the date range filter.
    defaultQuery: { // This is the default cube query for single chart with measures, dimensions and filters.
      measures: [
        'variations.count'
      ],
      dimensions: [
        'variations.reportingside'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'claim'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.CLAIMS_VALUE_CONTRACTOR]: {
    component: CHART_COMPONENTS.CLAIMS_VALUE_CONTRACTOR,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      filters: [{
        member: 'variations.type',
        operator: 'equals',
        values: [
          'claim'
        ]
      },
      {
        member: 'variations.reportingside',
        operator: 'equals',
        values: [
          'contractor'
        ]
      },
      {
        member: 'variations.category',
        operator: 'notEquals',
        values: [
          ''
        ]
      }
      ],
      measures: [
        'variations_parts.agreedvaluecents'
      ],
      dimensions: [
        'variations.category',
        'parts.currency'
      ]
    }
  },
  [CHART_NAMES.CLAIMS_VALUE_EMPLOYER]: {
    component: CHART_COMPONENTS.CLAIMS_VALUE_EMPLOYER,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      filters: [{
        member: 'variations.type',
        operator: 'equals',
        values: [
          'claim'
        ]
      },
      {
        member: 'variations.reportingside',
        operator: 'equals',
        values: [
          'employer'
        ]
      },
      {
        member: 'variations.category',
        operator: 'notEquals',
        values: [
          ''
        ]
      }
      ],
      measures: [
        'variations_parts.agreedvaluecents'
      ],
      dimensions: [
        'variations.category',
        'parts.currency'
      ]
    }
  },
  [CHART_NAMES.CLAIMS_VALUE_AND_COUNT]: {
    component: CHART_COMPONENTS.CLAIMS_VALUE_AND_COUNT,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      dimensions: [
        'companies.companyname',
        'parts.currency'
      ],
      measures: [
        'variations_parts.agreedvaluecents',
        'variations.count'
      ],
      filters: [{
        member: 'variations.type',
        operator: 'equals',
        values: [
          'claim'
        ]
      }]
    }
  },
  [CHART_NAMES.CLAIMS_PER_STATUS]: {
    component: CHART_COMPONENTS.CLAIMS_PER_STATUS,
    chartWidth: '300px',
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations.count'
      ],
      dimensions: [
        'variations.type',
        'variations.status'
      ],
      filters: [{
        member: 'variations.type',
        operator: 'equals',
        values: [
          'claim'
        ]
      }]
    }
  },
  [CHART_NAMES.EOT_PER_FINALIZED_CLAIMS]: {
    component: CHART_COMPONENTS.EOT_PER_FINALIZED_CLAIMS,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations_parts.eotagreed'
      ],
      dimensions: [
        'contracts.contractname',
        'contracts.id',
        'variations.name',
        'variations.id',
        'variations.type',
        'variations.status'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'claim'
          ]
        },
        {
          member: 'variations.status',
          operator: 'equals',
          values: [
            'FIN'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.CLAIMS_TREND]: {
    component: CHART_COMPONENTS.CLAIMS_TREND,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      dimensions: [
        'variations.type',
        'variations.status',
        'variations.id',
        'variations.name',
        'variations.createdat',
        'parts.currency'
      ],
      measures: [
        'variations.count',
        'variations_parts.agreedvaluecents',
        'variations_parts.estimatedvaluecents'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'claim'
          ]
        },
        {
          member: 'variations.status',
          operator: 'equals',
          values: [
            'FIN'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.VARIATIONS_PER_STATUS]: {
    component: CHART_COMPONENTS.VARIATIONS_PER_STATUS,
    chartWidth: '400px',
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations.count'
      ],
      dimensions: [
        'variations.status'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'variation'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.VARIATIONS_PER_STAGE]: {
    component: CHART_COMPONENTS.VARIATIONS_PER_STAGE,
    chartWidth: '300px',
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations.count'
      ],
      dimensions: [
        'variations.stage'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'variation'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.VARIATIONS_PER_ROOT_CAUSES]: {
    component: CHART_COMPONENTS.VARIATIONS_PER_ROOT_CAUSES,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations.count'
      ],
      dimensions: [
        'variations.category'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'variation'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.VARIATIONS_TIME_IMPACT_PER_ROOT_CAUSES]: {
    component: CHART_COMPONENTS.VARIATIONS_TIME_IMPACT_PER_ROOT_CAUSES,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations_parts.eotagreed'
      ],
      dimensions: [
        'variations.category'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'variation'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.VARIATIONS_FINANCIAL_IMPACT_PER_ROOT_CAUSES]: {
    component: CHART_COMPONENTS.VARIATIONS_FINANCIAL_IMPACT_PER_ROOT_CAUSES,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      measures: [
        'variations_parts.agreedvaluecents'
      ],
      dimensions: [
        'variations.category',
        'parts.currency'
      ],
      filters: [
        {
          member: 'variations.type',
          operator: 'equals',
          values: [
            'variation'
          ]
        }
      ]
    }
  },
  [CHART_NAMES.VARIATIONS_VALUE_AND_COUNT]: {
    component: CHART_COMPONENTS.VARIATIONS_VALUE_AND_COUNT,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      dimensions: [
        'companies.companyname',
        'parts.currency'
      ],
      measures: [
        'variations.count',
        'variations_parts.agreedvaluecents'
      ],
      filters: [{
        member: 'variations.type',
        operator: 'equals',
        values: [
          'variation'
        ]
      }]
    }
  },
  [CHART_NAMES.VARIATIONS_ACTUAL_IMPACT_IN_DAYS]: {
    component: CHART_COMPONENTS.VARIATIONS_ACTUAL_IMPACT_IN_DAYS,
    defaultTimeDimension: 'variations.receiveddate',
    defaultQuery: {
      dimensions: [
        'variations.category',
        'variations.status',
        'variations.stage',
        'variations.type'
      ],
      measures: [
        'variations_parts.eotagreed'

      ],
      filters: [
        {
          values: [
            'FIN'
          ],
          member: 'variations.status',
          operator: 'equals'
        },
        {
          values: [
            '2'
          ],
          member: 'variations.stage',
          operator: 'equals'
        },
        {
          values: [
            'variation'
          ],
          member: 'variations.type',
          operator: 'equals'
        }
      ]
    }
  },
  [CHART_NAMES.CONTRACTS_PAID_AND_UNPAID]: {
    component: CHART_COMPONENTS.CONTRACTS_PAID_AND_UNPAID,
    chartWidth: '240px',
    defaultTimeDimension: 'contracts.createdat',
    defaultQuery: {
      dimensions: [
        'contracts.id',
        'parts.currency'

      ],
      measures: [
        'parts.signedprice',
        'parts.adjustedprice',
        'parts.currentPrice'
      ]
    }
  },
  [CHART_NAMES.CONTRACTS_ONGOING_AND_CLOSED]: {
    component: CHART_COMPONENTS.CONTRACTS_ONGOING_AND_CLOSED,
    chartWidth: '240px',
    defaultTimeDimension: 'contracts.createdat',
    defaultQuery: {
      measures: [
        'contracts.count'
      ],
      dimensions: [
        'contracts.active'
      ]
    }
  },
  [CHART_NAMES.CONTRACTS_SIGNED_AND_ADJUSTED_PRICES]: {
    component: CHART_COMPONENTS.CONTRACTS_SIGNED_AND_ADJUSTED_PRICES,
    chartWidth: '300px',
    defaultTimeDimension: 'contracts.createdat',
    defaultQuery: {
      measures: [
        'parts.signedprice',
        'parts.adjustedprice'
      ],
      dimensions: [
        'parts.currency'
      ]
    }
  },
  [CHART_NAMES.LDS_VALUE_AND_COUNT_CONTRACTOR]: {
    component: CHART_COMPONENTS.LDS_VALUE_AND_COUNT_CONTRACTOR,
    defaultTimeDimension: 'liquidated_damages.updatedat',
    defaultQuery: {
      dimensions: [
        'companies.companyname',
        'parts.currency'
      ],
      measures: [
        'liquidated_damages.count',
        'liquidated_damages.totalvaluecents'
      ]
    }
  }
}
